/**
 * Result + error types. Endpoints never throw — `call` collapses
 * thrown exceptions into `{ kind: "network" }` and HTTP errors into
 * whatever shape `endpoint.parseError` produces.
 */

export type Result<T, E> = { ok: true; data: T } | { ok: false; error: E };

export type NetworkError = { kind: "network"; message: string };

/**
 * Default HTTP error envelope mirroring the server's
 * `{ code, message }` shape. Individual endpoints can narrow this
 * with a discriminated union when they want callers to switch on
 * specific codes (e.g. sign-in's INVALID_EMAIL_OR_PASSWORD).
 */
export type ApiError = {
  kind: "http";
  status: number;
  code: string | undefined;
  message: string;
};

/**
 * Lift a JSON error body into `ApiError`. Tolerates non-JSON
 * (data === null) and missing fields.
 */
export const parseApiError = (status: number, data: unknown): ApiError => {
  const body = (data ?? {}) as { code?: string; error?: string; message?: string };
  return {
    kind: "http",
    status,
    code: body.code ?? body.error,
    message: body.message ?? body.error ?? `Request failed (${status})`,
  };
};

export const ok = <T>(data: T): Result<T, never> => ({ ok: true, data });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });
